import api from './api';
import { Zone, CreateZoneDto, UpdateZoneDto, ZoneStats } from '../types/zone';
import { handleApiError } from '../utils/apiErrorHandler';

// Helper function to convert camelCase to snake_case
const toSnakeCase = (obj: any): any => {
  if (obj === null || typeof obj !== 'object') return obj;
  
  if (Array.isArray(obj)) {
    return obj.map(item => toSnakeCase(item));
  }
  
  return Object.keys(obj).reduce((acc: any, key) => {
    const snakeKey = key.replace(/([A-Z])/g, '_$1').toLowerCase();
    acc[snakeKey] = toSnakeCase(obj[key]);
    return acc;
  }, {});
};

// Helper function to convert snake_case to camelCase
// Special handling for zone fields to maintain consistency
const toCamelCase = (obj: any): any => {
  if (obj === null || typeof obj !== 'object') return obj;
  
  if (Array.isArray(obj)) {
    return obj.map(item => toCamelCase(item));
  }
  
  return Object.keys(obj).reduce((acc: any, key) => {
    // Keep zone fields in snake_case for consistency with component expectations
    if (key === 'price_per_kg' || key === 'created_at' || key === 'updated_at') {
      acc[key] = obj[key];
    } else {
      const camelKey = key.replace(/(_\w)/g, (k) => k[1].toUpperCase());
      acc[camelKey] = toCamelCase(obj[key]);
    }
    return acc;
  }, {});
};

class ZoneService {
  /**
   * Get all zones (public endpoint)
   */
  async getZones(): Promise<Zone[]> {
    try {
      console.log('[ZoneService] Fetching zones...');
      const response = await api.get('/zones');
      
      // Convert snake_case to camelCase
      const zones = (response.data || []).map((zone: any) => toCamelCase(zone));
      
      console.log(`[ZoneService] Fetched ${zones.length} zones`);
      return zones;
    } catch (error) {
      const apiError = handleApiError(error, 'Failed to fetch zones');
      console.error(`[ZoneService] Error: ${apiError.message}`, apiError.details);
      throw apiError;
    }
  }

  /**
   * Create a new zone (admin only)
   */
  async createZone(zoneData: CreateZoneDto): Promise<Zone> {
    try {
      console.log('[ZoneService] Creating zone with data:', zoneData);
      
      // Convert camelCase to snake_case for the API
      const snakeCaseData = toSnakeCase(zoneData);
      const response = await api.post('/zones', snakeCaseData);
      
      const zone = toCamelCase(response.data) as Zone;
      console.log('[ZoneService] Successfully created zone:', zone);
      
      return zone;
    } catch (error) {
      const apiError = handleApiError(error, 'Failed to create zone');
      console.error(`[ZoneService] Error: ${apiError.message}`, apiError.details);
      throw apiError;
    }
  }

  /**
   * Update a zone by ID (admin only)
   */
  async updateZone(id: number, zoneData: UpdateZoneDto): Promise<Zone> {
    try {
      console.log(`[ZoneService] Updating zone ${id} with data:`, zoneData);
      
      // Convert camelCase to snake_case for the API
      const snakeCaseData = toSnakeCase(zoneData);
      const response = await api.put(`/zones/${id}`, snakeCaseData);
      
      const zone = toCamelCase(response.data) as Zone;
      console.log(`[ZoneService] Successfully updated zone ${id}:`, zone);
      
      return zone;
    } catch (error) {
      const apiError = handleApiError(error, `Failed to update zone ${id}`);
      console.error(`[ZoneService] Error: ${apiError.message}`, apiError.details);
      throw apiError;
    }
  }

  /**
   * Delete a zone by ID (admin only)
   */
  async deleteZone(id: number): Promise<void> {
    try {
      console.log(`[ZoneService] Deleting zone ${id}`);
      await api.delete(`/zones/${id}`);
      console.log(`[ZoneService] Successfully deleted zone ${id}`);
    } catch (error) {
      const apiError = handleApiError(error, `Failed to delete zone ${id}`);
      console.error(`[ZoneService] Error: ${apiError.message}`, apiError.details);
      throw apiError;
    }
  }

  /**
   * Get zone statistics
   */
  async getZoneStats(): Promise<ZoneStats> {
    try {
      console.log('[ZoneService] Fetching zone statistics...');
      const zones = await this.getZones();
      
      // Calculate statistics from zones data
      const nationalZones = zones.filter(zone => zone.region.toLowerCase().includes('national'));
      const internationalZones = zones.filter(zone => !zone.region.toLowerCase().includes('national'));
      
      // Calculate average price, handling invalid/missing values
      const validZones = zones.filter(zone => 
        typeof zone.price_per_kg === 'number' && !isNaN(zone.price_per_kg) && zone.price_per_kg > 0
      );
      
      const stats: ZoneStats = {
        total: zones.length,
        national: nationalZones.length,
        international: internationalZones.length,
        averagePrice: validZones.length > 0 
          ? validZones.reduce((sum, zone) => sum + zone.price_per_kg, 0) / validZones.length
          : 0
      };
      
      // Round to 2 decimal places for display
      stats.averagePrice = parseFloat(stats.averagePrice.toFixed(2));
      
      console.log('[ZoneService] Zone statistics:', stats);
      return stats;
    } catch (error) {
      const apiError = handleApiError(error, 'Failed to fetch zone statistics');
      console.error(`[ZoneService] Error: ${apiError.message}`, apiError.details);
      throw apiError;
    }
  }
}

export default new ZoneService();
